import { useEffect, useRef, useState } from 'react';
import * as logistaService from '@/services/logistaService';

const categories = [
  { value: 'gastronomia', label: 'Gastronomia' },
  { value: 'comercio', label: 'Comércio' },
  { value: 'cultura', label: 'Cultura' },
  { value: 'saude', label: 'Saúde & Bem-estar' },
  { value: 'servicos', label: 'Serviços' },
  { value: 'turismo', label: 'Turismo' },
  { value: 'entretenimento', label: 'Entretenimento' },
];

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

export default function LogistaLojaPage() {
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const logoRef = useRef(null);
  const coverRef = useRef(null);

  useEffect(() => {
    logistaService.getLoja()
      .then((r) => setForm(r.data.data))
      .catch(() => setMsg({ type: 'error', text: 'Não foi possível carregar a loja.' }))
      .finally(() => setLoading(false));
  }, []);

  const set = (key, val) => setForm((f) => ({ ...f, [key]: val }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMsg(null);
    try {
      const r = await logistaService.updateLoja(form);
      setForm(r.data.data);
      setMsg({ type: 'success', text: 'Loja atualizada com sucesso!' });
    } catch {
      setMsg({ type: 'error', text: 'Erro ao guardar. Tenta novamente.' });
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const r = await logistaService.uploadLogo(file);
      setForm((f) => ({ ...f, logoUrl: r.data.data.logoUrl }));
      setMsg({ type: 'success', text: 'Logo atualizado!' });
    } catch {
      setMsg({ type: 'error', text: 'Erro ao fazer upload do logo.' });
    }
  };

  const handleCoverUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const r = await logistaService.uploadCover(file);
      setForm((f) => ({ ...f, imageUrl: r.data.data.imageUrl }));
      setMsg({ type: 'success', text: 'Foto de capa atualizada!' });
    } catch {
      setMsg({ type: 'error', text: 'Erro ao fazer upload da capa.' });
    }
  };

  const useCurrentLocation = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition((pos) => {
      setForm((f) => ({ ...f, lat: pos.coords.latitude, lng: pos.coords.longitude }));
    });
  };

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-accent border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-serif text-2xl font-black text-fg">A minha loja</h1>
        <p className="text-sm text-muted">Edita as informações que os jogadores vêem no jogo.</p>
      </div>

      {/* Images */}
      <div className="rounded-2xl border border-edge bg-surface p-5 shadow-sm">
        <h2 className="mb-4 font-bold text-fg">Imagens</h2>
        <div className="flex flex-wrap gap-6">
          <div>
            <p className="mb-2 text-xs font-semibold text-muted uppercase tracking-wide">Logo</p>
            <div className="flex items-end gap-3">
              {form?.logoUrl ? (
                <img
                  src={`${API_URL}${form.logoUrl}`}
                  alt="Logo"
                  className="h-20 w-20 rounded-xl border border-edge object-cover shadow-sm"
                />
              ) : (
                <div className="flex h-20 w-20 items-center justify-center rounded-xl border-2 border-dashed border-edge bg-background text-3xl">
                  🏪
                </div>
              )}
              <button
                type="button"
                onClick={() => logoRef.current?.click()}
                className="rounded-xl border border-accent/30 bg-accent/5 px-3 py-2 text-xs font-semibold text-accent hover:bg-accent/10"
              >
                Alterar
              </button>
              <input ref={logoRef} type="file" accept="image/*" className="hidden" onChange={handleLogoUpload} />
            </div>
          </div>

          <div>
            <p className="mb-2 text-xs font-semibold text-muted uppercase tracking-wide">Foto de capa</p>
            <div className="flex items-end gap-3">
              {form?.imageUrl ? (
                <img
                  src={`${API_URL}${form.imageUrl}`}
                  alt="Capa"
                  className="h-20 w-36 rounded-xl border border-edge object-cover shadow-sm"
                />
              ) : (
                <div className="flex h-20 w-36 items-center justify-center rounded-xl border-2 border-dashed border-edge bg-background text-3xl">
                  🖼️
                </div>
              )}
              <button
                type="button"
                onClick={() => coverRef.current?.click()}
                className="rounded-xl border border-accent/30 bg-accent/5 px-3 py-2 text-xs font-semibold text-accent hover:bg-accent/10"
              >
                Alterar
              </button>
              <input ref={coverRef} type="file" accept="image/*" className="hidden" onChange={handleCoverUpload} />
            </div>
          </div>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={save} className="rounded-2xl border border-edge bg-surface p-5 shadow-sm">
        <h2 className="mb-4 font-bold text-fg">Informações da loja</h2>

        <div className="grid gap-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1.5 block text-sm font-semibold text-fg">Nome da loja *</label>
              <input
                type="text"
                required
                value={form?.name || ''}
                onChange={(e) => set('name', e.target.value)}
                className="w-full rounded-xl border border-edge bg-background px-4 py-2.5 text-sm outline-none focus:border-accent focus:ring-2 focus:ring-accent/20"
              />
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-semibold text-fg">Categoria</label>
              <select
                value={form?.category || 'gastronomia'}
                onChange={(e) => set('category', e.target.value)}
                className="w-full rounded-xl border border-edge bg-background px-4 py-2.5 text-sm outline-none focus:border-accent focus:ring-2 focus:ring-accent/20"
              >
                {categories.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className="mb-1.5 block text-sm font-semibold text-fg">Descrição</label>
            <textarea
              rows={3}
              value={form?.description || ''}
              onChange={(e) => set('description', e.target.value)}
              placeholder="Descreve a tua loja para os jogadores..."
              className="w-full resize-none rounded-xl border border-edge bg-background px-4 py-2.5 text-sm outline-none focus:border-accent focus:ring-2 focus:ring-accent/20"
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1.5 block text-sm font-semibold text-fg">Morada</label>
              <input
                type="text"
                value={form?.address || ''}
                onChange={(e) => set('address', e.target.value)}
                className="w-full rounded-xl border border-edge bg-background px-4 py-2.5 text-sm outline-none focus:border-accent focus:ring-2 focus:ring-accent/20"
              />
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-semibold text-fg">Email da loja</label>
              <input
                type="email"
                value={form?.email || ''}
                onChange={(e) => set('email', e.target.value)}
                className="w-full rounded-xl border border-edge bg-background px-4 py-2.5 text-sm outline-none focus:border-accent focus:ring-2 focus:ring-accent/20"
              />
            </div>
          </div>

          <div>
            <label className="mb-1.5 block text-sm font-semibold text-fg">Telefone</label>
            <input
              type="tel"
              value={form?.phone || ''}
              onChange={(e) => set('phone', e.target.value)}
              className="w-full rounded-xl border border-edge bg-background px-4 py-2.5 text-sm outline-none focus:border-accent focus:ring-2 focus:ring-accent/20"
            />
          </div>

          <div>
            <div className="mb-1.5 flex items-center justify-between">
              <label className="text-sm font-semibold text-fg">Localização GPS</label>
              <button
                type="button"
                onClick={useCurrentLocation}
                className="text-xs font-semibold text-accent hover:underline"
              >
                📍 Usar localização atual
              </button>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <input
                type="number"
                step="any"
                value={form?.lat || ''}
                onChange={(e) => set('lat', e.target.value)}
                placeholder="Latitude"
                className="w-full rounded-xl border border-edge bg-background px-4 py-2.5 text-sm outline-none focus:border-accent focus:ring-2 focus:ring-accent/20"
              />
              <input
                type="number"
                step="any"
                value={form?.lng || ''}
                onChange={(e) => set('lng', e.target.value)}
                placeholder="Longitude"
                className="w-full rounded-xl border border-edge bg-background px-4 py-2.5 text-sm outline-none focus:border-accent focus:ring-2 focus:ring-accent/20"
              />
            </div>
            <p className="mt-1 text-xs text-muted">As coordenadas permitem aos jogadores encontrar a tua loja no mapa.</p>
          </div>
        </div>

        {msg && (
          <p className={`mt-4 rounded-xl px-4 py-2.5 text-sm font-semibold ${msg.type === 'success' ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-600'}`}>
            {msg.text}
          </p>
        )}

        <button
          type="submit"
          disabled={saving}
          className="mt-4 rounded-xl bg-accent px-6 py-2.5 text-sm font-bold text-white hover:bg-accent disabled:opacity-60"
        >
          {saving ? 'A guardar...' : 'Guardar alterações'}
        </button>
      </form>
    </div>
  );
}
